import React, { useMemo, useState } from "react";
import { INSP_STATUS, kindLabel } from "@/lib/inspectionConstants";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function eventColor(status) {
  return (INSP_STATUS[status] || {}).dot || (status === "erledigt" ? "#16A34A" : "#2563EB");
}

export default function InspectionCalendar({ inspections, records, onEventClick }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  const events = useMemo(() => {
    const ev = [];
    (inspections || []).forEach((i) => {
      if (i.next_due) ev.push({ id: i.id, date: i.next_due.slice(0, 10), title: i.title, status: i.display_status, category: i.category, building: i.building, kind: i.kind, type: "due", ref: i });
    });
    (records || []).forEach((r) => {
      if (r.actual_date) ev.push({ id: r.id, date: r.actual_date.slice(0, 10), title: r.title, status: "erledigt", category: r.category, building: r.building, kind: r.kind, type: "done", ref: r });
    });
    return ev;
  }, [inspections, records]);

  const byDate = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const shift = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "year") d.setFullYear(d.getFullYear() + dir);
    setCursor(d);
  };

  const EventChip = ({ e }) => (
    <button onClick={() => onEventClick?.(e)} data-testid={`cal-event-${e.id}`}
      className="w-full text-left text-[11px] px-1.5 py-0.5 rounded truncate flex items-center gap-1 hover:opacity-80"
      style={{ background: `${eventColor(e.status)}1a`, color: eventColor(e.status) }}>
      {e.type === "done" && <CheckCircle2 className="h-2.5 w-2.5 shrink-0" />}
      <span className="truncate">{e.title}</span>
    </button>
  );

  const title = view === "year" ? cursor.getFullYear()
    : view === "month" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "day" ? cursor.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : (() => { const s = startOfWeek(cursor); const e = addDays(s, 6); return `${s.getDate()}.${s.getMonth() + 1}. – ${e.getDate()}.${e.getMonth() + 1}.${e.getFullYear()}`; })();

  const todayIso = iso(new Date());

  const renderMonth = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return (
      <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
        {DAYS.map((d) => <div key={d} className="bg-slate-50 text-center text-xs font-semibold text-slate-500 py-1.5">{d}</div>)}
        {cells.map((d) => {
          const di = iso(d); const inMonth = d.getMonth() === cursor.getMonth(); const evs = byDate[di] || [];
          return (
            <div key={di} className={`bg-white min-h-[92px] p-1 ${inMonth ? "" : "opacity-40"}`} data-testid={`cal-day-${di}`}>
              <div className={`text-xs mb-0.5 ${di === todayIso ? "font-bold text-primary" : "text-slate-400"}`}>{d.getDate()}</div>
              <div className="space-y-0.5">{evs.slice(0, 4).map((e) => <EventChip key={e.id + e.type} e={e} />)}{evs.length > 4 && <div className="text-[10px] text-slate-400">+{evs.length - 4} weitere</div>}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeek = () => {
    const start = startOfWeek(cursor);
    const cols = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return (
      <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
        {cols.map((d) => { const di = iso(d); const evs = byDate[di] || []; return (
          <div key={di} className="bg-white min-h-[160px] p-1.5" data-testid={`cal-day-${di}`}>
            <div className={`text-xs mb-1 ${di === todayIso ? "font-bold text-primary" : "text-slate-500"}`}>{DAYS[(d.getDay() + 6) % 7]} {d.getDate()}.</div>
            <div className="space-y-0.5">{evs.map((e) => <EventChip key={e.id + e.type} e={e} />)}</div>
          </div>
        ); })}
      </div>
    );
  };

  const renderDay = () => {
    const di = iso(cursor); const evs = byDate[di] || [];
    return (
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid={`cal-day-${di}`}>
        {evs.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">Keine Termine an diesem Tag.</div>
          : evs.map((e) => (
            <button key={e.id + e.type} onClick={() => onEventClick?.(e)} className="w-full text-left p-3 flex items-center gap-2 hover:bg-slate-50" data-testid={`cal-event-${e.id}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: eventColor(e.status) }} />
              <span className="font-medium text-slate-800 text-sm">{e.title}</span>
              <span className="text-xs text-slate-400">{kindLabel(e.kind)}{e.category ? ` · ${e.category}` : ""}</span>
            </button>
          ))}
      </div>
    );
  };

  const renderYear = () => {
    const y = cursor.getFullYear();
    const countByMonth = Array.from({ length: 12 }, (_, m) => events.filter((e) => { const dt = new Date(e.date); return dt.getFullYear() === y && dt.getMonth() === m; }));
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {MONTHS.map((mn, m) => {
          const evs = countByMonth[m];
          const overdue = evs.filter((e) => e.status === "ueberfaellig").length;
          return (
            <button key={mn} onClick={() => { setCursor(new Date(y, m, 1)); setView("month"); }} data-testid={`cal-month-${m}`}
              className="rounded-lg border border-slate-200 p-3 text-left hover:border-primary/40 hover:bg-slate-50">
              <div className="font-semibold text-slate-800 text-sm">{mn}</div>
              <div className="text-2xl font-mono-tech font-bold text-slate-900 mt-1">{evs.length}</div>
              <div className="text-xs text-slate-400">Termine{overdue ? <span className="text-red-600"> · {overdue} überfällig</span> : ""}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderList = () => {
    const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
    return (
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
        {sorted.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">Keine Termine.</div>
          : sorted.map((e) => (
            <button key={e.id + e.type} onClick={() => onEventClick?.(e)} className="w-full text-left p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`cal-event-${e.id}`}>
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: eventColor(e.status) }} />
              <span className="text-sm font-mono-tech text-slate-500 w-24 shrink-0">{e.date}</span>
              <span className="font-medium text-slate-800 text-sm flex-1 truncate">{e.title}</span>
              <span className="text-xs text-slate-400 hidden sm:block">{kindLabel(e.kind)}{e.category ? ` · ${e.category}` : ""}</span>
            </button>
          ))}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="inspection-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {view !== "list" && <>
            <button onClick={() => shift(-1)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => shift(1)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Heute</Button>
          </>}
          <span className="font-head font-bold text-slate-900 ml-2">{title}</span>
        </div>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-36" data-testid="cal-view"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Monat</SelectItem>
            <SelectItem value="week">Woche</SelectItem>
            <SelectItem value="day">Tag</SelectItem>
            <SelectItem value="year">Jahr</SelectItem>
            <SelectItem value="list">Liste</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {view === "month" && renderMonth()}
      {view === "week" && renderWeek()}
      {view === "day" && renderDay()}
      {view === "year" && renderYear()}
      {view === "list" && renderList()}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500 pt-1">
        {Object.entries(INSP_STATUS).map(([k, v]) => <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: v.dot }} />{v.label}</span>)}
      </div>
    </div>
  );
}
